import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import { dropIndex, moveItem, shiftOf } from "./reorder";

/**
 * DragList lets a list be put in order by dragging its rows.
 *
 * It replaces a pair of up and down buttons on every row. Two buttons is four
 * taps to move a chapter three places and a round trip to the server for each
 * of them, and at phone width they took a third of the row away from the title.
 *
 * Pointer events, not HTML5 drag and drop. `draggable` does nothing at all on a
 * touch screen, and this is a studio driven from a phone.
 *
 * What the pointer does:
 *
 *  - The handle captures the pointer, so a finger that slides off the row —
 *    which it will, on a narrow screen — keeps dragging rather than dropping.
 *  - `touch-action: none` on the handle and only on the handle. Without it the
 *    browser scrolls the page instead of reporting the move; on the whole row
 *    it would make the list unscrollable.
 *  - The order is committed once, on release. Committing on every crossing
 *    would be a request per row passed, and an order the author was still
 *    deciding on.
 *
 * And what the keyboard does, which is the half that is easy to leave out: the
 * handle is a button, and the arrow keys move the row it belongs to. Dragging
 * is the only way to reach this otherwise, and a drag is not something every
 * hand can do.
 */
export default function DragList<T extends { id: string }>({
  items,
  disabled = false,
  onReorder,
  children,
  testID,
}: {
  items: T[];
  /** Whether rows can be moved at all. */
  disabled?: boolean;
  /** Called with the new order once, when a drag or a key press settles it. */
  onReorder: (ids: string[]) => void;
  /** Renders one row. `handle` goes on whatever starts the drag. */
  children: (item: T, index: number, handle: DragHandle) => React.ReactNode;
  testID?: string;
}) {
  const t = useT();
  const list = useRef<HTMLOListElement | null>(null);
  /**
   * The drag in progress: where it started, where it would land, and the rows'
   * resting geometry, measured once when the finger went down.
   *
   * Measured once on purpose. Reading the rows' rectangles during the move
   * would read positions this very drag has already shifted, and the answer
   * would feed back into itself.
   */
  const [drag, setDrag] = useState<{
    from: number;
    to: number;
    offset: number;
    centres: number[];
    sizes: number[];
  } | null>(null);

  /**
   * The drag and the props, mirrored into refs.
   *
   * Two reasons, and the second is the one that bites. The window handlers live
   * for the whole drag and would otherwise close over the first state — that is
   * the usual one. But `items` and `onReorder` change identity on every render,
   * and a pointer move re-renders: depending on them would tear down and
   * re-attach three window listeners sixty times a second for the length of
   * the drag. The effect depends on *whether* a drag is happening and nothing
   * else, so it subscribes once per drag.
   */
  const current = useRef(drag);
  current.current = drag;
  const latest = useRef({ items, onReorder });
  latest.current = { items, onReorder };

  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const d = current.current;
      if (!d) return;
      const offset = e.clientY - d.offset;
      const centre = d.centres[d.from]! + offset;
      setDrag({ ...d, to: dropIndex(d.centres, d.from, centre) });
      // Kept on the element rather than in state: it changes every frame, and
      // re-rendering the whole list at pointer rate drops frames on a phone.
      const row = list.current?.children[d.from] as HTMLElement | undefined;
      if (row) row.style.transform = `translateY(${offset}px)`;
    };
    const finish = (commit: boolean) => {
      const d = current.current;
      setDrag(null);
      if (!d || !commit || d.to === d.from) return;
      const { items, onReorder } = latest.current;
      onReorder(moveItem(items, d.from, d.to).map((i) => i.id));
    };
    const up = () => finish(true);
    const cancel = () => finish(false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
  }, [dragging]);

  const start = (index: number) => (e: React.PointerEvent) => {
    if (disabled || items.length < 2) return;
    const rows = Array.from(list.current?.children ?? []) as HTMLElement[];
    const boxes = rows.map((r) => r.getBoundingClientRect());
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({
      from: index,
      to: index,
      offset: e.clientY,
      centres: boxes.map((b) => b.top + b.height / 2),
      sizes: boxes.map((b) => b.height),
    });
  };

  const byKey = (index: number) => (e: React.KeyboardEvent) => {
    const delta = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
    if (!delta || disabled) return;
    const to = index + delta;
    if (to < 0 || to >= items.length) return;
    e.preventDefault();
    onReorder(moveItem(items, index, to).map((i) => i.id));
  };

  return (
    <ol ref={list} className="mb-10 space-y-2" data-testid={testID}>
      {items.map((item, index) => {
        const held = drag?.from === index;
        const shift = drag ? shiftOf(drag.sizes, drag.from, drag.to, index) : 0;
        return (
          <li
            key={item.id}
            data-dragging={held || undefined}
            className={
              held
                ? // Lifted out of the flow so it passes over its neighbours
                  // rather than under them, and left without a transition so it
                  // tracks the finger exactly.
                  "relative z-20 touch-none shadow-lg"
                : "relative transition-transform duration-150"
            }
            style={held ? undefined : { transform: `translateY(${shift}px)` }}
          >
            {children(item, index, {
              "aria-label": t.book.moveChapter,
              "data-testid": "drag-handle",
              disabled: disabled || items.length < 2,
              // On the handle and nowhere else: without it the browser
              // scrolls the page and never reports the move, and on the row
              // it would make the list itself unscrollable.
              style: { touchAction: "none" },
              onPointerDown: start(index),
              onKeyDown: byKey(index),
            })}
          </li>
        );
      })}
    </ol>
  );
}

/** DragHandle is spread onto the button a row is dragged by. */
export interface DragHandle {
  "aria-label": string;
  "data-testid": string;
  disabled: boolean;
  style: React.CSSProperties;
  onPointerDown: (e: React.PointerEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}
